import React, { useEffect, useMemo, useRef, useState } from "react";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Checkbox from "../../components/common/Checkbox";
import Input from "../../components/common/Input";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Select from "../../components/common/Select";
import Table from "../../components/common/Table";
import Textarea from "../../components/common/Textarea";
import TimePicker from "../../components/common/TimePicker";
import useClientPagination from "../../hooks/useClientPagination";
import {
  useCreateReservationExceptionMutation,
  useDeleteReservationExceptionMutation,
  useGenerateReservationSlotsMutation,
  useGetReservationExceptionsQuery,
  // useGetReservationPolicyQuery,
  useGetReservationSettingsQuery,
  useGetReservationSlotsQuery,
  useGetReservationWeeklySlotsQuery,
  useUpdateReservationExceptionMutation,
  useUpdateReservationSlotCapacityMutation,
  // useUpsertReservationPolicyMutation,
  useUpsertReservationSettingsMutation,
} from "../../store/api";
import { useStore } from "../../context/StoreContext";

const today = new Date().toISOString().slice(0, 10);
const defaultExceptionTo = (() => {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return date.toISOString().slice(0, 10);
})();

const weekdayOptions = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const weekdayValueByLabel = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
};

const weekdayDropdownOptions = weekdayOptions.map((day) => ({
  id: String(weekdayValueByLabel[day]),
  label: day.charAt(0) + day.slice(1).toLowerCase(),
}));

// eslint-disable-next-line no-unused-vars
const weekdayLabelByValue = {
  1: "MONDAY",
  2: "TUESDAY",
  3: "WEDNESDAY",
  4: "THURSDAY",
  5: "FRIDAY",
  6: "SATURDAY",
  7: "SUNDAY",
};

// eslint-disable-next-line no-unused-vars
const emptyPolicyForm = {
  isReservationEnabled: "true",
  isOnlineBookingEnabled: "true",
  isPaidBookingRequired: "false",
  paymentType: "",
  amountPerSeat: "",
  fixedAmount: "",
  cancellationCutoffMinutes: "",
  refundAllowed: "false",
  noShowGraceMinutes: "",
  minAdvanceBookingMinutes: "",
  maxAdvanceBookingDays: "",
  maxGuestsPerBooking: "",
  onlineCapacityBuffer: "",
  isActive: "true",
};

const emptySettingsForm = {
  startTime: "",
  endTime: "",
  slotIntervalMinutes: "15",
  advanceBookingDays: "",
  defaultSeatCapacity: "",
  onlineSeatCapacity: "",
  isActive: "true",
  effectiveFrom: "",
  effectiveTo: "",
};

const emptyExceptionForm = {
  exceptionType: "1",
  reservationDate: "",
  slotTime: "",
  overrideCapacity: "",
  reason: "",
};

const emptyGenerateForm = {
  openTime: "",
  closeTime: "",
  slotIntervalMinutes: "15",
  totalSeatCapacity: "",
  onlineSeatCapacity: "",
  isOpen: "true",
  isOnlineOpen: "true",
};

const toBooleanString = (value, fallback = "false") => {
  if (value === true || value === "true") return "true";
  if (value === false || value === "false") return "false";
  return fallback;
};

const toBoolean = (value, fallback = false) => {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return fallback;
};

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const toTimeValue = (time) => {
  if (!time) return null;
  const [hours = "0", minutes = "0", seconds = "0"] = time.split(":");
  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
};

const normalizePagedResponse = (data) => {
  if (Array.isArray(data)) {
    return { items: data, totalCount: data.length };
  }

  const items =
    data?.items ||
    data?.content ||
    data?.results ||
    data?.records ||
    data?.data ||
    [];

  return {
    items: Array.isArray(items) ? items : [],
    totalCount:
      data?.totalCount ??
      data?.totalElements ??
      data?.count ??
      (Array.isArray(items) ? items.length : 0),
  };
};

const getApiErrorMessage = (error, fallback) =>
  error?.data?.detail ||
  error?.data?.message ||
  error?.error ||
  fallback;

const getSlotId = (slot) =>
  slot?.reservationSlotId || slot?.slotId || slot?.id || slot?.slotTime;

const getExceptionId = (exception) =>
  exception?.reservationExceptionId || exception?.exceptionId || exception?.id;

const formatExceptionType = (value) => {
  const map = {
    1: "Entire day closed",
    2: "Slot closed",
    3: "Capacity override",
    4: "Online booking disabled",
    FULL_DAY_CLOSED: "Entire day closed",
    SLOT_CLOSED: "Slot closed",
    CAPACITY_OVERRIDE: "Capacity override",
    ONLINE_BOOKING_DISABLED: "Online booking disabled",
  };
  return map[value] || value || "Unknown";
};

// eslint-disable-next-line no-unused-vars
const buildPolicyForm = (policy) => ({
  isReservationEnabled: toBooleanString(policy?.isReservationEnabled, "true"),
  isOnlineBookingEnabled: toBooleanString(policy?.isOnlineBookingEnabled, "true"),
  isPaidBookingRequired: toBooleanString(policy?.isPaidBookingRequired, "false"),
  paymentType: policy?.paymentType || "",
  amountPerSeat: policy?.amountPerSeat ?? "",
  fixedAmount: policy?.fixedAmount ?? "",
  cancellationCutoffMinutes: policy?.cancellationCutoffMinutes ?? "",
  refundAllowed: toBooleanString(policy?.refundAllowed, "false"),
  noShowGraceMinutes: policy?.noShowGraceMinutes ?? "",
  minAdvanceBookingMinutes: policy?.minAdvanceBookingMinutes ?? "",
  maxAdvanceBookingDays: policy?.maxAdvanceBookingDays ?? "",
  maxGuestsPerBooking: policy?.maxGuestsPerBooking ?? "",
  onlineCapacityBuffer: policy?.onlineCapacityBuffer ?? "",
  isActive: toBooleanString(policy?.isActive, "true"),
});

const buildSettingsForm = (settings) => ({
  startTime: settings?.startTime || "",
  endTime: settings?.endTime || "",
  slotIntervalMinutes: `${settings?.slotIntervalMinutes ?? 15}`,
  advanceBookingDays: settings?.advanceBookingDays ?? "",
  defaultSeatCapacity: settings?.defaultSeatCapacity ?? "",
  onlineSeatCapacity: settings?.onlineSeatCapacity ?? "",
  isActive: toBooleanString(settings?.isActive, "true"),
  effectiveFrom: settings?.effectiveFrom || "",
  effectiveTo: settings?.effectiveTo || "",
});

const buildExceptionForm = (exception) => ({
  exceptionType: `${exception?.exceptionType ?? 1}`,
  reservationDate: exception?.reservationDate || exception?.date || "",
  slotTime: exception?.slotTime || "",
  overrideCapacity: exception?.overrideCapacity ?? "",
  reason: exception?.reason || "",
});

const validateNonNegative = (value) =>
  value === "" || value === null || value === undefined || Number(value) >= 0;

// eslint-disable-next-line no-unused-vars
const validatePolicyForm = (form) => {
  const errors = [];

  if (!validateNonNegative(form.amountPerSeat)) {
    errors.push("Amount per seat must be 0 or more.");
  }
  if (!validateNonNegative(form.fixedAmount)) {
    errors.push("Fixed amount must be 0 or more.");
  }
  if (!validateNonNegative(form.minAdvanceBookingMinutes)) {
    errors.push("Minimum advance booking minutes must be 0 or more.");
  }
  if (!validateNonNegative(form.maxAdvanceBookingDays)) {
    errors.push("Maximum advance booking days must be 0 or more.");
  }
  if (!validateNonNegative(form.onlineCapacityBuffer)) {
    errors.push("Online capacity buffer must be 0 or more.");
  }
  if (form.maxGuestsPerBooking !== "" && Number(form.maxGuestsPerBooking) <= 0) {
    errors.push("Maximum guests per booking must be greater than 0.");
  }
  if (toBoolean(form.isPaidBookingRequired) && !form.paymentType.trim()) {
    errors.push("Payment type is required when paid booking is enabled.");
  }

  return errors;
};

const validateSettingsForm = (form) => {
  const errors = [];
  const start = toTimeValue(form.startTime);
  const end = toTimeValue(form.endTime);

  if (!form.startTime || !form.endTime) {
    errors.push("Start time and end time are required.");
  }
  if (start !== null && end !== null && end <= start) {
    errors.push("End time must be after start time.");
  }
  if (!form.slotIntervalMinutes || Number(form.slotIntervalMinutes) <= 0) {
    errors.push("Slot interval must be greater than 0.");
  }
  if (!validateNonNegative(form.advanceBookingDays)) {
    errors.push("Advance booking days must be 0 or more.");
  }
  if (!validateNonNegative(form.defaultSeatCapacity)) {
    errors.push("Default seat capacity must be 0 or more.");
  }
  if (form.effectiveFrom && form.effectiveTo && form.effectiveFrom > form.effectiveTo) {
    errors.push("Effective from cannot be after effective to.");
  }

  return errors;
};

const validateExceptionForm = (form) => {
  const errors = [];
  if (!form.reservationDate) {
    errors.push("Date is required.");
  }
  if (form.exceptionType !== "1" && !form.slotTime) {
    errors.push("Slot time is required for slot-level exceptions.");
  }
  if (form.exceptionType === "3") {
    if (form.overrideCapacity === "") {
      errors.push("Override capacity is required for capacity overrides.");
    } else if (Number(form.overrideCapacity) < 0) {
      errors.push("Override capacity must be 0 or more.");
    }
  }
  return errors;
};

const validateGenerateForm = (form) => {
  const errors = [];
  const start = toTimeValue(form.openTime);
  const end = toTimeValue(form.closeTime);

  if (!form.openTime || !form.closeTime) {
    errors.push("Open time and close time are required.");
  }
  if (start !== null && end !== null && end <= start) {
    errors.push("Close time must be later than open time.");
  }
  if (!form.slotIntervalMinutes || Number(form.slotIntervalMinutes) <= 0) {
    errors.push("Slot interval must be greater than 0.");
  }
  if (form.totalSeatCapacity === "" || Number(form.totalSeatCapacity) < 0) {
    errors.push("Total capacity must be 0 or more.");
  }
  if (!validateNonNegative(form.onlineSeatCapacity)) {
    errors.push("Online capacity must be 0 or more.");
  }
  if (
    form.totalSeatCapacity !== "" &&
    form.onlineSeatCapacity !== "" &&
    Number(form.onlineSeatCapacity) > Number(form.totalSeatCapacity)
  ) {
    errors.push("Online capacity cannot exceed total capacity.");
  }

  return errors;
};

const surfaceCardClass =
  "rounded-[28px] border border-white/70 bg-white/95 shadow-[0_18px_45px_rgba(15,23,42,0.10)]";

const ALERT_TIMEOUT_MS = 5000;

const sectionTitles = {
  weeklySlots: "Create Reservation",
  exceptions: "Create Exception",
  settings: "Reservation Settings",
};

const sectionSubtitles = {
  weeklySlots: "Create and manage reservation slots for the selected weekdays.",
  exceptions: "Add one-off exceptions for specific dates, times, or capacities.",
  settings: "Set your store's default reservation timing, capacity, and availability.",
};

function useTimedAlert() {
  const [alert, setAlert] = useState(null);
  const timeoutRef = useRef(null);

  const clearAlert = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setAlert(null);
  };

  const showAlert = (tone, message) => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    setAlert({ tone, message });
    timeoutRef.current = window.setTimeout(() => {
      setAlert(null);
      timeoutRef.current = null;
    }, ALERT_TIMEOUT_MS);
  };

  useEffect(() => () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
  }, []);

  return { alert, showAlert, clearAlert };
}

function WeekdayMultiSelect({ options, selectedValues, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const allSelected = selectedValues.length === options.length;

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.id))
    .map((option) => option.label);

  const toggleValue = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((item) => item !== value));
      return;
    }
    onChange([...selectedValues, value]);
  };

  const toggleAll = () => {
    onChange(allSelected ? [] : options.map((option) => option.id));
  };

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-2 block text-sm text-slate-600">Weekdays</label>
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left text-base text-slate-900 shadow-sm transition hover:border-slate-300 focus:border-slate-400 focus:outline-none"
      >
        <span className="truncate">
          {selectedLabels.length === 0
            ? "Select weekdays"
            : allSelected
              ? "All weekdays selected"
              : selectedLabels.join(", ")}
        </span>
        <span className="ml-4 text-slate-400">{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 z-20 mt-2 rounded-xl border border-slate-200 bg-white p-4 shadow-[0_18px_45px_rgba(15,23,42,0.14)]">
          <div className="space-y-3">
            <Checkbox
              label="Select all"
              checked={allSelected}
              onChange={toggleAll}
            />
            <div className="border-t border-slate-100 pt-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {options.map((option) => (
                  <Checkbox
                    key={option.id}
                    label={option.label}
                    checked={selectedValues.includes(option.id)}
                    onChange={() => toggleValue(option.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReservationSettingsFields({ form, onChange }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 xl:gap-6">
        <TimePicker
          label="Open time"
          layout="inline"
          className="w-full max-w-[440px]"
          value={form.startTime}
          onChange={(value) => onChange({ startTime: value })}
        />
        <TimePicker
          label="Close time"
          layout="inline"
          className="w-full max-w-[440px]"
          value={form.endTime}
          minTimeExclusive={form.startTime}
          onChange={(value) => onChange({ endTime: value })}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Input
          label="Slot interval (mins)"
          type="number"
          min="1"
          className="max-w-[220px]"
          value={form.slotIntervalMinutes}
          onChange={(event) => onChange({ slotIntervalMinutes: event.target.value })}
        />
        <Input
          label="Advance booking days"
          type="number"
          min="0"
          className="max-w-[220px]"
          value={form.advanceBookingDays}
          onChange={(event) => onChange({ advanceBookingDays: event.target.value })}
        />
        <Select
          label="Status"
          className="max-w-[220px]"
          value={form.isActive}
          onChange={(event) => onChange({ isActive: event.target.value })}
        >
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </Select>
        <Input
          label="Default seat capacity"
          type="number"
          min="0"
          className="max-w-[220px]"
          value={form.defaultSeatCapacity}
          onChange={(event) => onChange({ defaultSeatCapacity: event.target.value })}
        />
        <Input
          label="Effective from"
          type="date"
          className="max-w-[220px]"
          value={form.effectiveFrom}
          onChange={(event) => onChange({ effectiveFrom: event.target.value })}
        />
        <Input
          label="Effective to"
          type="date"
          className="max-w-[220px]"
          value={form.effectiveTo}
          onChange={(event) => onChange({ effectiveTo: event.target.value })}
        />
      </div>
    </div>
  );
}

export default function Reservations({ section = "weeklySlots" }) {
  const { selectedStoreId } = useStore();
  const pageTitle = sectionTitles[section] || "Reservations";
  const pageSubtitle = sectionSubtitles[section];

  const [settingsForm, setSettingsForm] = useState(emptySettingsForm);
  const [settingsErrors, setSettingsErrors] = useState([]);
  // const [policyForm, setPolicyForm] = useState(emptyPolicyForm);
  // const [policyErrors, setPolicyErrors] = useState([]);

  const [weeklySlotDays, setWeeklySlotDays] = useState(["1"]);
  const [exceptionRange, setExceptionRange] = useState({
    from: today,
    to: defaultExceptionTo,
  });
  const [exceptionForm, setExceptionForm] = useState(emptyExceptionForm);
  const [exceptionErrors, setExceptionErrors] = useState([]);
  const [editingExceptionId, setEditingExceptionId] = useState(null);

  const [availabilityDate, setAvailabilityDate] = useState(today);
  const [generateForm, setGenerateForm] = useState(emptyGenerateForm);
  const [generateErrors, setGenerateErrors] = useState([]);
  const [slotDrafts, setSlotDrafts] = useState({});
  const settingsAlert = useTimedAlert();
  const generateAlert = useTimedAlert();
  const slotAlert = useTimedAlert();
  const exceptionAlert = useTimedAlert();

  // const {
  //   data: policyData,
  //   isLoading: isPolicyLoading,
  //   isError: isPolicyError,
  //   error: policyError,
  // } = useGetReservationPolicyQuery(
  //   { storeId: selectedStoreId },
  //   { skip: !selectedStoreId },
  // );
  // const [upsertReservationPolicy, policySaveState] =
  //   useUpsertReservationPolicyMutation();

  const {
    data: settingsData,
    isLoading: isSettingsLoading,
    isError: isSettingsError,
    error: settingsError,
  } = useGetReservationSettingsQuery(
    { storeId: selectedStoreId },
    { skip: !selectedStoreId },
  );
  const [upsertReservationSettings, settingsSaveState] =
    useUpsertReservationSettingsMutation();
  const activeWeeklySlotDay = weeklySlotDays[0] || "1";
  const { refetch: refetchWeeklySlots } = useGetReservationWeeklySlotsQuery(
    { storeId: selectedStoreId, dayOfWeek: Number(activeWeeklySlotDay) },
    { skip: !selectedStoreId },
  );

  const {
    data: exceptionsData,
    isLoading: isExceptionsLoading,
    isError: isExceptionsError,
    error: exceptionsError,
    refetch: refetchExceptions,
  } = useGetReservationExceptionsQuery(
    {
      storeId: selectedStoreId,
      from: exceptionRange.from,
      to: exceptionRange.to,
    },
    { skip: !selectedStoreId },
  );
  const [createReservationException, createExceptionState] =
    useCreateReservationExceptionMutation();
  const [updateReservationException, updateExceptionState] =
    useUpdateReservationExceptionMutation();
  const [deleteReservationException, deleteExceptionState] =
    useDeleteReservationExceptionMutation();

  const {
    data: slotsData,
    isLoading: isSlotsLoading,
    isError: isSlotsError,
    error: slotsError,
    refetch: refetchSlots,
  } = useGetReservationSlotsQuery(
    { storeId: selectedStoreId, date: availabilityDate },
    { skip: !selectedStoreId || !availabilityDate },
  );
  const [generateReservationSlots, generateSlotsState] =
    useGenerateReservationSlotsMutation();
  const [updateReservationSlotCapacity, updateSlotState] =
    useUpdateReservationSlotCapacityMutation();

  // useEffect(() => {
  //   if (policyData) {
  //     setPolicyForm(buildPolicyForm(policyData));
  //   }
  // }, [policyData]);

  useEffect(() => {
    if (settingsData) {
      setSettingsForm(buildSettingsForm(settingsData));
    }
  }, [settingsData]);

  useEffect(() => {
    setSlotDrafts({});
  }, [slotsData, availabilityDate, selectedStoreId]);

  const availabilitySlots = useMemo(() => {
    const { items } = normalizePagedResponse(slotsData);
    return items;
  }, [slotsData]);

  const {
    page: slotPage,
    pageSize: slotPageSize,
    totalCount: slotTotalCount,
    paginatedItems: paginatedSlots,
    setPage: setSlotPage,
    setPageSize: setSlotPageSize,
  } = useClientPagination(availabilitySlots, {
    pageSize: 10,
    resetKey: `${selectedStoreId || ""}-${availabilityDate}`,
  });

  const exceptionList = useMemo(() => normalizePagedResponse(exceptionsData).items, [exceptionsData]);
  // const updatePolicyForm = (patch) => {
  //   const next = { ...policyForm, ...patch };
  //   if (patch.isPaidBookingRequired === "false") {
  //     next.paymentType = "";
  //     next.amountPerSeat = "";
  //     next.fixedAmount = "";
  //   }
  //   setPolicyForm(next);
  // };

  const updateSettingsForm = (patch) => {
    setSettingsForm((prev) => ({ ...prev, ...patch }));
  };

  const resetExceptionEditor = () => {
    setEditingExceptionId(null);
    setExceptionForm(emptyExceptionForm);
    setExceptionErrors([]);
  };

  // const handleSavePolicy = async () => {
  //   const errors = validatePolicyForm(policyForm);
  //   setPolicyErrors(errors);
  //   if (errors.length > 0 || !selectedStoreId) return;

  //   await upsertReservationPolicy({
  //     storeId: selectedStoreId,
  //     payload: {
  //       isReservationEnabled: toBoolean(policyForm.isReservationEnabled, true),
  //       isOnlineBookingEnabled: toBoolean(policyForm.isOnlineBookingEnabled, true),
  //       isPaidBookingRequired: toBoolean(policyForm.isPaidBookingRequired, false),
  //       paymentType: toBoolean(policyForm.isPaidBookingRequired, false)
  //         ? policyForm.paymentType || "NONE"
  //         : "NONE",
  //       amountPerSeat: Number(policyForm.amountPerSeat || 0),
  //       fixedAmount: Number(policyForm.fixedAmount || 0),
  //       cancellationCutoffMinutes: Number(policyForm.cancellationCutoffMinutes || 0),
  //       refundAllowed: toBoolean(policyForm.refundAllowed, false),
  //       noShowGraceMinutes: Number(policyForm.noShowGraceMinutes || 0),
  //       minAdvanceBookingMinutes: Number(policyForm.minAdvanceBookingMinutes || 0),
  //       maxAdvanceBookingDays: Number(policyForm.maxAdvanceBookingDays || 0),
  //       maxGuestsPerBooking: Number(policyForm.maxGuestsPerBooking || 0),
  //       onlineCapacityBuffer: Number(policyForm.onlineCapacityBuffer || 0),
  //       isActive: toBoolean(policyForm.isActive, true),
  //     },
  //   }).unwrap();
  // };

  const handleSaveSettings = async () => {
    if (!selectedStoreId) return;
    const errors = validateSettingsForm(settingsForm);
    setSettingsErrors(errors);
    if (errors.length > 0) return;

    try {
      await upsertReservationSettings({
        storeId: selectedStoreId,
        payload: {
          startTime: settingsForm.startTime,
          endTime: settingsForm.endTime,
          slotIntervalMinutes: Number(settingsForm.slotIntervalMinutes || 15),
          advanceBookingDays: Number(settingsForm.advanceBookingDays || 0),
          defaultSeatCapacity: Number(settingsForm.defaultSeatCapacity || 0),
          onlineSeatCapacity: toNumberOrNull(settingsForm.onlineSeatCapacity),
          isActive: toBoolean(settingsForm.isActive, true),
          effectiveFrom: settingsForm.effectiveFrom || null,
          effectiveTo: settingsForm.effectiveTo || null,
        },
      }).unwrap();
      settingsAlert.showAlert("success", "Reservation settings saved successfully.");
    } catch (error) {
      settingsAlert.showAlert(
        "error",
        getApiErrorMessage(error, "Failed to save reservation settings."),
      );
    }
  };

  const handleSaveException = async () => {
    if (!selectedStoreId) return;

    const errors = validateExceptionForm(exceptionForm);
    setExceptionErrors(errors);
    if (errors.length > 0) return;

    const payload = {
      exceptionType: Number(exceptionForm.exceptionType),
      reservationDate: exceptionForm.reservationDate,
      slotTime: exceptionForm.exceptionType === "1" ? null : exceptionForm.slotTime,
      overrideCapacity:
        exceptionForm.exceptionType === "3"
          ? Number(exceptionForm.overrideCapacity)
          : null,
      reason: exceptionForm.reason || null,
    };

    try {
      if (editingExceptionId) {
        await updateReservationException({
          storeId: selectedStoreId,
          exceptionId: editingExceptionId,
          payload,
        }).unwrap();
      } else {
        await createReservationException({
          storeId: selectedStoreId,
          payload,
        }).unwrap();
      }

      exceptionAlert.showAlert("success", "Reservation exception saved successfully.");
      resetExceptionEditor();
      await refetchExceptions();
      await refetchSlots();
    } catch (error) {
      exceptionAlert.showAlert(
        "error",
        getApiErrorMessage(error, "Failed to save reservation exception."),
      );
    }
  };

  const handleDeleteException = async (exception) => {
    const exceptionId = getExceptionId(exception);
    if (!selectedStoreId || !exceptionId) return;
    const confirmed = window.confirm("Delete this reservation exception?");
    if (!confirmed) return;

    await deleteReservationException({
      storeId: selectedStoreId,
      exceptionId,
    }).unwrap();
    await refetchExceptions();
    await refetchSlots();
  };

  const handleGenerateSlots = async () => {
    if (!selectedStoreId) return;
    const errors = validateGenerateForm(generateForm);
    if (weeklySlotDays.length === 0) {
      errors.unshift("Select at least one weekday.");
    }
    setGenerateErrors(errors);
    if (errors.length > 0) return;

    try {
      await generateReservationSlots({
        storeId: selectedStoreId,
        payload: {
          dayOfWeeks: weeklySlotDays.map((dayOfWeek) => Number(dayOfWeek)),
          openTime: generateForm.openTime,
          closeTime: generateForm.closeTime,
          slotIntervalMinutes: Number(generateForm.slotIntervalMinutes),
          totalSeatCapacity: Number(generateForm.totalSeatCapacity || 0),
          onlineSeatCapacity: toNumberOrNull(generateForm.onlineSeatCapacity),
          isOpen: toBoolean(generateForm.isOpen, true),
          isOnlineOpen: toBoolean(generateForm.isOnlineOpen, true),
        },
      }).unwrap();

      generateAlert.showAlert("success", "Weekly slots generated successfully.");
      setGenerateForm(emptyGenerateForm);
      setGenerateErrors([]);
      await refetchWeeklySlots();
      await refetchSlots();
    } catch (error) {
      generateAlert.showAlert(
        "error",
        getApiErrorMessage(error, "Failed to generate weekly slots."),
      );
    }
  };

  const getSlotDraft = (slot) => {
    const slotId = getSlotId(slot);
    const draft = slotDrafts[slotId] || {};
    return {
      totalSeatCapacity: draft.totalSeatCapacity ?? slot.totalSeatCapacity ?? 0,
      onlineSeatCapacity: draft.onlineSeatCapacity ?? slot.onlineSeatCapacity ?? 0,
      isOpen: draft.isOpen ?? toBooleanString(slot.isOpen, "true"),
      isOnlineOpen: draft.isOnlineOpen ?? toBooleanString(slot.isOnlineOpen, "true"),
    };
  };

  const updateSlotDraft = (slot, patch) => {
    const slotId = getSlotId(slot);
    setSlotDrafts((prev) => ({
      ...prev,
      [slotId]: {
        ...(prev[slotId] || {}),
        ...patch,
      },
    }));
  };

  const handleSaveSlot = async (slot) => {
    if (!selectedStoreId) return;
    const draft = getSlotDraft(slot);
    try {
      await updateReservationSlotCapacity({
        storeId: selectedStoreId,
        slotId: getSlotId(slot),
        date: availabilityDate,
        payload: {
          totalSeatCapacity: Number(draft.totalSeatCapacity || 0),
          onlineSeatCapacity: Number(draft.onlineSeatCapacity || 0),
          isOpen: toBoolean(draft.isOpen, true),
          isOnlineOpen: toBoolean(draft.isOnlineOpen, true),
        },
      }).unwrap();
      slotAlert.showAlert("success", "Operational slot updated successfully.");
      await refetchSlots();
    } catch (error) {
      slotAlert.showAlert(
        "error",
        getApiErrorMessage(error, "Failed to update reservation slot."),
      );
    }
  };

  const slotColumns = [
    { key: "time", label: "Time" },
    { key: "totalSeatCapacity", label: "Total capacity" },
    { key: "booked", label: <div className="text-center">Booked</div> },
    { key: "available", label: <div className="text-center">Available</div> },
    { key: "isOpen", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const slotRows = paginatedSlots.map((slot) => {
    const draft = getSlotDraft(slot);
    return {
      id: getSlotId(slot),
      time: slot.slotTime,
      totalSeatCapacity: (
        <Input
          type="number"
          min="0"
          className="w-24"
          value={draft.totalSeatCapacity}
          onChange={(event) => updateSlotDraft(slot, { totalSeatCapacity: event.target.value })}
        />
      ),
      booked: <div className="text-center">{slot.bookedSeats ?? 0}</div>,
      available: <div className="text-center">{slot.availableSeats ?? 0}</div>,
      isOpen: (
        <Select
          value={draft.isOpen}
          className="w-32"
          onChange={(event) => updateSlotDraft(slot, { isOpen: event.target.value })}
        >
          <option value="true">Open</option>
          <option value="false">Closed</option>
        </Select>
      ),
      actions: (
        <Button
          size="sm"
          disabled={updateSlotState.isLoading}
          onClick={() => handleSaveSlot(slot)}
        >
          Save
        </Button>
      ),
    };
  });

  const exceptionColumns = [
    { key: "reservationDate", label: "Date" },
    { key: "exceptionType", label: "Type" },
    { key: "slotTime", label: "Slot time" },
    { key: "overrideCapacity", label: "Override capacity" },
    { key: "reason", label: "Reason" },
    { key: "actions", label: "Actions" },
  ];

  const exceptionRows = exceptionList.map((exception) => ({
    id: getExceptionId(exception),
    reservationDate: exception.reservationDate || exception.date,
    exceptionType: formatExceptionType(exception.exceptionType),
    slotTime: exception.slotTime || "-",
    overrideCapacity:
      exception.overrideCapacity === null || exception.overrideCapacity === undefined
        ? "-"
        : exception.overrideCapacity,
    reason: exception.reason || "-",
    actions: (
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setEditingExceptionId(getExceptionId(exception));
            setExceptionForm(buildExceptionForm(exception));
            setExceptionErrors([]);
          }}
        >
          Edit
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={deleteExceptionState.isLoading}
          onClick={() => handleDeleteException(exception)}
        >
          Delete
        </Button>
      </div>
    ),
  }));

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Reservations"
          subtitle="Choose a store to manage reservation policy, schedule, exceptions, availability, and bookings."
        />
        <Alert tone="warning">Select a store first to work with reservation settings.</Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={pageTitle} subtitle={pageSubtitle} />

      {/* {!toBoolean(policyForm.isReservationEnabled, true) && (
        <Alert tone="warning">
          Reservations are globally disabled for this store. Booking flows should remain blocked until policy is re-enabled.
        </Alert>
      )} */}

      {section === "settings" && (
        <Card className={surfaceCardClass}>
          <div className="space-y-6">
            {isSettingsError && (
              <Alert tone="error">
                {getApiErrorMessage(settingsError, "Failed to load reservation settings.")}
              </Alert>
            )}
            {settingsErrors.length > 0 && <Alert tone="error">{settingsErrors[0]}</Alert>}
            {settingsAlert.alert && (
              <Alert tone={settingsAlert.alert.tone}>{settingsAlert.alert.message}</Alert>
            )}

            <ReservationSettingsFields form={settingsForm} onChange={updateSettingsForm} />

            <div className="flex justify-end">
              <Button
                disabled={isSettingsLoading || settingsSaveState.isLoading}
                onClick={handleSaveSettings}
              >
                {settingsSaveState.isLoading ? "Saving..." : "Save settings"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* {activeTab === "policy" && (
        <Card className={surfaceCardClass}>
          <div className="space-y-6">
            <div className={sectionIntroClass}>
              <h2 className="text-lg font-semibold text-slate-900">Reservation policy</h2>
              <p className="mt-1 text-sm text-slate-600">
                Control whether reservations are allowed and define booking, payment, and guest guardrails.
              </p>
            </div>

            {isPolicyError && (
              <Alert tone="error">
                {getApiErrorMessage(policyError, "Failed to load reservation policy.")}
              </Alert>
            )}
            {policyErrors.length > 0 && (
              <Alert tone="error">{policyErrors[0]}</Alert>
            )}
            {policySaveState.isSuccess && (
              <Alert tone="success">Reservation policy saved successfully.</Alert>
            )}
            {policySaveState.isError && (
              <Alert tone="error">
                {getApiErrorMessage(policySaveState.error, "Failed to save reservation policy.")}
              </Alert>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Select
                label="Reservations enabled"
                value={policyForm.isReservationEnabled}
                onChange={(event) =>
                  updatePolicyForm({ isReservationEnabled: event.target.value })
                }
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </Select>
              <Select
                label="Online booking enabled"
                value={policyForm.isOnlineBookingEnabled}
                onChange={(event) =>
                  updatePolicyForm({ isOnlineBookingEnabled: event.target.value })
                }
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </Select>
              <Select
                label="Paid booking required"
                value={policyForm.isPaidBookingRequired}
                onChange={(event) =>
                  updatePolicyForm({ isPaidBookingRequired: event.target.value })
                }
              >
                <option value="true">Required</option>
                <option value="false">Not required</option>
              </Select>
              <Select
                label="Policy status"
                value={policyForm.isActive}
                onChange={(event) => updatePolicyForm({ isActive: event.target.value })}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
              <Input
                label="Payment type"
                placeholder="For example: FIXED or PER_SEAT"
                disabled={!toBoolean(policyForm.isPaidBookingRequired, false)}
                value={policyForm.paymentType}
                onChange={(event) => updatePolicyForm({ paymentType: event.target.value })}
              />
              <Input
                label="Amount per seat"
                type="number"
                min="0"
                disabled={!toBoolean(policyForm.isPaidBookingRequired, false)}
                value={policyForm.amountPerSeat}
                onChange={(event) => updatePolicyForm({ amountPerSeat: event.target.value })}
              />
              <Input
                label="Fixed amount"
                type="number"
                min="0"
                disabled={!toBoolean(policyForm.isPaidBookingRequired, false)}
                value={policyForm.fixedAmount}
                onChange={(event) => updatePolicyForm({ fixedAmount: event.target.value })}
              />
              <Select
                label="Refund allowed"
                value={policyForm.refundAllowed}
                onChange={(event) => updatePolicyForm({ refundAllowed: event.target.value })}
              >
                <option value="true">Allowed</option>
                <option value="false">Not allowed</option>
              </Select>
              <Input
                label="Cancellation cutoff (mins)"
                type="number"
                min="0"
                value={policyForm.cancellationCutoffMinutes}
                onChange={(event) =>
                  updatePolicyForm({ cancellationCutoffMinutes: event.target.value })
                }
              />
              <Input
                label="No-show grace (mins)"
                type="number"
                min="0"
                value={policyForm.noShowGraceMinutes}
                onChange={(event) => updatePolicyForm({ noShowGraceMinutes: event.target.value })}
              />
              <Input
                label="Min advance booking (mins)"
                helper="How early a guest can book before the slot."
                type="number"
                min="0"
                value={policyForm.minAdvanceBookingMinutes}
                onChange={(event) =>
                  updatePolicyForm({ minAdvanceBookingMinutes: event.target.value })
                }
              />
              <Input
                label="Max advance booking (days)"
                helper="Limit how far into the future reservations are allowed."
                type="number"
                min="0"
                value={policyForm.maxAdvanceBookingDays}
                onChange={(event) =>
                  updatePolicyForm({ maxAdvanceBookingDays: event.target.value })
                }
              />
              <Input
                label="Max guests per booking"
                helper="Guests above this count should be blocked in the UI."
                type="number"
                min="1"
                value={policyForm.maxGuestsPerBooking}
                onChange={(event) =>
                  updatePolicyForm({ maxGuestsPerBooking: event.target.value })
                }
              />
              <Input
                label="Online capacity buffer"
                helper="Keep this many seats back from online booking."
                type="number"
                min="0"
                value={policyForm.onlineCapacityBuffer}
                onChange={(event) =>
                  updatePolicyForm({ onlineCapacityBuffer: event.target.value })
                }
              />
            </div>

            <div className="flex justify-end">
              <Button
                disabled={isPolicyLoading || policySaveState.isLoading}
                onClick={handleSavePolicy}
              >
                {policySaveState.isLoading ? "Saving..." : "Save policy"}
              </Button>
            </div>
          </div>
        </Card>
      )} */}

      {section === "weeklySlots" && (
        <Card className={surfaceCardClass}>
          <div className="space-y-6">
           

            {generateErrors.length > 0 && <Alert tone="error">{generateErrors[0]}</Alert>}
            {generateAlert.alert && (
              <Alert tone={generateAlert.alert.tone}>{generateAlert.alert.message}</Alert>
            )}

            <div className="grid grid-cols-1 gap-6">
              <Card className="rounded-[24px] border border-slate-100/80 bg-slate-50/45 p-6 shadow-[0_12px_28px_rgba(15,23,42,0.06)]">
                <div className="space-y-5">
                  <WeekdayMultiSelect
                    options={weekdayDropdownOptions}
                    selectedValues={weeklySlotDays}
                    onChange={setWeeklySlotDays}
                  />

                  <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 xl:gap-6">
                    <TimePicker
                      label="Open time"
                      layout="inline"
                      className="w-full max-w-[440px]"
                      value={generateForm.openTime}
                      onChange={(value) =>
                        setGenerateForm((prev) => ({ ...prev, openTime: value }))
                      }
                    />
                    <TimePicker
                      label="Close time"
                      layout="inline"
                      className="w-full max-w-[440px]"
                      value={generateForm.closeTime}
                      minTimeExclusive={generateForm.openTime}
                      onChange={(value) =>
                        setGenerateForm((prev) => ({ ...prev, closeTime: value }))
                      }
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-[repeat(3,minmax(0,220px))_minmax(260px,1fr)] xl:items-end">
                    <Input
                      label="Slot interval (mins)"
                      type="number"
                      className="max-w-[220px]"
                      min="1"
                      value={generateForm.slotIntervalMinutes}
                      onChange={(event) =>
                        setGenerateForm((prev) => ({
                          ...prev,
                          slotIntervalMinutes: event.target.value,
                        }))
                      }
                    />
                    <Input
                      label="Total capacity"
                      type="number"
                      className="max-w-[220px]"
                      min="0"
                      value={generateForm.totalSeatCapacity}
                      onChange={(event) =>
                        setGenerateForm((prev) => ({
                          ...prev,
                          totalSeatCapacity: event.target.value,
                        }))
                      }
                    />
                    <Select
                      label="Slot open"
                      className="max-w-[220px]"
                      value={generateForm.isOpen}
                      onChange={(event) =>
                        setGenerateForm((prev) => ({ ...prev, isOpen: event.target.value }))
                      }
                    >
                      <option value="true">Open</option>
                      <option value="false">Closed</option>
                    </Select>
                    <div className="flex xl:justify-end">
                      <Button
                        disabled={generateSlotsState.isLoading}
                        onClick={handleGenerateSlots}
                      >
                        {generateSlotsState.isLoading ? "Generating..." : "Generate weekday slots"}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className={surfaceCardClass}>
                <div className="space-y-5">
                  <div className="flex justify-end">
                    <Input
                      label="Reservation date"
                      type="date"
                      value={availabilityDate}
                      onChange={(event) => setAvailabilityDate(event.target.value)}
                    />
                  </div>

                  {isSlotsError && (
                    <Alert tone="error">
                      {getApiErrorMessage(slotsError, "Failed to load reservation slots.")}
                    </Alert>
                  )}
                  {slotAlert.alert && (
                    <Alert tone={slotAlert.alert.tone}>{slotAlert.alert.message}</Alert>
                  )}

                  <Table
                    columns={slotColumns}
                    rows={slotRows}
                    tableClassName="w-full table-fixed text-left"
                    headCellClassName="pr-2 text-sm"
                    bodyCellClassName="pr-2 align-middle"
                    emptyText="No slots available for this date."
                  />
                  {slotTotalCount > 0 && (
                    <Pagination
                      page={slotPage}
                      pageSize={slotPageSize}
                      totalCount={slotTotalCount}
                      onPageChange={setSlotPage}
                      onPageSizeChange={setSlotPageSize}
                      isLoading={isSlotsLoading}
                    />
                  )}
                </div>
              </Card>
            </div>
          </div>
        </Card>
      )}

      {section === "exceptions" && (
        <Card className={surfaceCardClass}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <Card className="rounded-[24px] border border-slate-100/80 bg-slate-50/45 p-6 shadow-[0_12px_28px_rgba(15,23,42,0.06)]">
                <div className="space-y-5">
                  {isExceptionsError && (
                    <Alert tone="error">
                      {getApiErrorMessage(exceptionsError, "Failed to load reservation exceptions.")}
                    </Alert>
                  )}
                  {exceptionErrors.length > 0 && <Alert tone="error">{exceptionErrors[0]}</Alert>}
                  {exceptionAlert.alert && (
                    <Alert tone={exceptionAlert.alert.tone}>
                      {exceptionAlert.alert.message}
                    </Alert>
                  )}

                  <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 xl:gap-6">
                    <Input
                      label="Exception type"
                      readOnly
                      className="w-full max-w-[440px]"
                      value="Entire day closed"
                    />
                    <Input
                      label="Reservation date"
                      type="date"
                      className="w-full max-w-[440px]"
                      value={exceptionForm.reservationDate}
                      onChange={(event) =>
                        setExceptionForm((prev) => ({
                          ...prev,
                          reservationDate: event.target.value,
                          slotTime: "",
                        }))
                      }
                    />
                  </div>

                  <Textarea
                    label="Reason"
                    value={exceptionForm.reason}
                    onChange={(event) =>
                      setExceptionForm((prev) => ({ ...prev, reason: event.target.value }))
                    }
                  />

                  <div className="flex flex-wrap justify-end gap-3">
                    {editingExceptionId && (
                      <Button variant="secondary" onClick={resetExceptionEditor}>
                        Cancel edit
                      </Button>
                    )}
                    <Button
                      disabled={createExceptionState.isLoading || updateExceptionState.isLoading}
                      onClick={handleSaveException}
                    >
                      {createExceptionState.isLoading || updateExceptionState.isLoading
                        ? "Saving..."
                        : editingExceptionId
                          ? "Update exception"
                          : "Create exception"}
                    </Button>
                  </div>
                </div>
              </Card>

              <Card className={surfaceCardClass}>
                <div className="space-y-5">
                  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Input
                        label="From"
                        type="date"
                        value={exceptionRange.from}
                        onChange={(event) =>
                          setExceptionRange((prev) => ({ ...prev, from: event.target.value }))
                        }
                      />
                      <Input
                        label="To"
                        type="date"
                        value={exceptionRange.to}
                        onChange={(event) =>
                          setExceptionRange((prev) => ({ ...prev, to: event.target.value }))
                        }
                      />
                    </div>
                    <div className="flex md:justify-end">
                      <Button variant="secondary" size="md" onClick={() => refetchExceptions()}>
                        Refresh
                      </Button>
                    </div>
                  </div>

                  <Table
                    columns={exceptionColumns}
                    rows={exceptionRows}
                    tableClassName="min-w-[760px] text-left"
                    emptyText="No exceptions found for the selected range."
                  />
                  {isExceptionsLoading && (
                    <p className="text-sm text-slate-500">Loading reservation exceptions...</p>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </Card>
      )}

    </div>
  );
}
